import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  PrimaryButton,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  PlanStatusNote,
  RECEIPT_CHECK_SUSPENDED_NOTE,
} from '~/components/common/PlanStatusNote'
import { PlanSubmissionStatusBadge } from '~/components/common/PlanSubmissionStatusBadge'
import {
  StatusHeaderCard,
  type StatusHeaderItem,
  statusHeaderValueStyle,
} from '~/components/planSubmit/StatusHeaderCard'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import {
  STATUS_BADGE_LABEL_KEYS,
  deriveStatusBadge,
} from '~/models/planSubmission/statusBadge'
import {
  showsSubmitBlockReasonText,
  submitBlockReasonKey,
} from '~/models/planSubmission/submitBlockReason'
import { formatTimestamp } from '~/pages/GenerationSalesPlanDetail/models/formatTimestamp'

/**
 * 提出ボタンを有効化するステータス。
 * spec (generation-sales-plan-detail.md AC-032〜036) 準拠: GENERATED（未提出）/
 * GENERATION_FAILED・REJECTED（生成失敗・差戻し = 再提出可能）で提出可能。
 * SEND_FAILED（送信失敗）もユーザー操作による再提出を許可する。
 * SUBMITTED / ACCEPTED / INCONSISTENT / GENERATING は無効化する。
 */
const SUBMITTABLE_STATUSES: ReadonlySet<PlanSubmissionStatus> = new Set([
  PlanSubmissionStatus.GENERATED,
  PlanSubmissionStatus.GENERATION_FAILED,
  PlanSubmissionStatus.SEND_FAILED,
  PlanSubmissionStatus.REJECTED,
])

export function isSubmittableStatus(status: PlanSubmissionStatus): boolean {
  return SUBMITTABLE_STATUSES.has(status)
}

type Props = {
  status: PlanSubmissionStatus
  submissionEntityCode: string
  submissionEntityName: string | undefined
  /** 作成日時。 */
  fileCreatedAt: Timestamp | undefined
  /** 最終提出日時。未提出なら undefined。 */
  submittedAt: Timestamp | undefined
  /** 前回提出（前世代）の提出日時。初回提出なら undefined。 */
  previousSubmittedAt: Timestamp | undefined
  /** 提出期限 (AC-PLC-020-1。backend 算出値)。期限を出さない断面 (当日・週間以降) は undefined。 */
  submissionDeadlineAt: Timestamp | undefined
  /** 提出中か（= mutation isPending）。ボタンを loading 表示にする。 */
  isSubmitting: boolean
  /**
   * workflow-back-bar-readonly AC-23: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき提出ボタンを disabled にする（表示切替系ではなく編集・確定操作のため）。
   */
  isReadonly?: boolean
  /** 「提出」ボタン押下（確認ダイアログを開く） */
  onSubmit: () => void
  /** OCCTO 呼び出し上限による受付照合停止中か (AC-PLC-058。backend 判定値をそのまま渡す)。 */
  receiptCheckSuspendedByCallLimit: boolean
}

/**
 * 発電販売計画詳細のヘッダー status block（design `ds-submit-meta` 相当）。
 *
 * カード枠・label+value グリッド・バッジ表示・タイムスタンプ行の共通構造は
 * `~/components/planSubmit/StatusHeaderCard` に集約（ProcurementPlanDetail と共有）。
 * 本ファイルはページ固有の項目定義（submissionEntityCode/Name の複合表示、提出ボタン）
 * のみを持つ薄い形。
 */
export const DetailHeader = ({
  status,
  submissionEntityCode,
  submissionEntityName,
  fileCreatedAt,
  submittedAt,
  previousSubmittedAt,
  submissionDeadlineAt,
  isSubmitting,
  isReadonly = false,
  onSubmit,
  receiptCheckSuspendedByCallLimit,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const badge = deriveStatusBadge(status)
  const canSubmit = isSubmittableStatus(status) && !isSubmitting && !isReadonly

  const timestampText = (v: Timestamp | undefined): string =>
    v !== undefined
      ? formatTimestamp(v)
      : t('page:generationSalesPlanDetail.header.empty')

  const items: StatusHeaderItem[] = [
    {
      key: 'status',
      label: t('page:generationSalesPlanDetail.header.status'),
      value: (
        <>
          <PlanSubmissionStatusBadge
            hasWarning={badge.hasWarning}
            labelKeys={STATUS_BADGE_LABEL_KEYS}
            variant={badge.variant}
          />
          <PlanStatusNote
            {...RECEIPT_CHECK_SUSPENDED_NOTE}
            visible={receiptCheckSuspendedByCallLimit}
          />
        </>
      ),
    },
    {
      key: 'submissionEntityCode',
      label: t('page:generationSalesPlanDetail.header.submissionEntityCode'),
      // [design 差異] 2026-08-28 デザイナー判断 (一覧の BG セル = PR #6758 /
      // #6699 と同基準)。 需要調達計画詳細のヘッダーと style が完全同一なので
      // 常に対で直す。 名称を本文と同じ濃さにし、 コードとの間隔を 8px に。
      // 主従はコード側の fontWeight 600 + monospace で付いている。
      // 8080 の plan/gen-sales-detail.html はそもそも名称を出していない
      // (コードのみ) ため、 この項目に 8080 の裏取りは無い。
      value: (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          <span
            style={{
              ...statusHeaderValueStyle,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {submissionEntityCode}
          </span>
          {submissionEntityName !== undefined &&
            submissionEntityName !== '' && (
              <span
                style={{
                  fontSize: fontSizes.md.value,
                  color: LightModeSemanticColors.Text.highEmphasis.value,
                }}
              >
                {submissionEntityName}
              </span>
            )}
        </span>
      ),
    },
    {
      key: 'created',
      label: t('page:generationSalesPlanDetail.header.created'),
      value: (
        <span style={statusHeaderValueStyle}>
          {timestampText(fileCreatedAt)}
        </span>
      ),
    },
    {
      key: 'lastSubmitted',
      label: t('page:generationSalesPlanDetail.header.lastSubmitted'),
      value: (
        <span style={statusHeaderValueStyle}>{timestampText(submittedAt)}</span>
      ),
    },
    {
      key: 'previousSubmitted',
      label: t('page:generationSalesPlanDetail.header.previousSubmitted'),
      value: (
        <span style={statusHeaderValueStyle}>
          {timestampText(previousSubmittedAt)}
        </span>
      ),
    },
    {
      key: 'submissionDeadline',
      label: t('page:generationSalesPlanDetail.header.submissionDeadline'),
      value: (
        <span style={statusHeaderValueStyle}>
          {timestampText(submissionDeadlineAt)}
        </span>
      ),
    },
  ]

  const submitButton = (
    <PrimaryButton
      disabled={!canSubmit}
      loading={isSubmitting}
      onClick={onSubmit}
      size="sm"
    >
      {t('page:generationSalesPlanDetail.submit.buttonLabel')}
    </PrimaryButton>
  )

  // 押せない理由 (AC-PLC-062): 非活性の時だけ、 ボタンの直下に右端をそろえて文で常に出す
  // (需要調達計画詳細と同じ。 ツールチップは補助)
  const blockReasonKey = canSubmit ? undefined : submitBlockReasonKey(status)
  const statusDisabledReason =
    blockReasonKey !== undefined ? t(blockReasonKey) : undefined
  // AC-WFW-014: 閲覧のみの時は画面固有の理由より読み取り専用の理由だけをツールチップで出す
  // (常時文は出さない。取得中・失敗中は readonlyReason が無く、従来の画面固有の理由に戻る)
  const { readonlyReason } = useWorkflowLockReasons()
  const submitDisabledReason = readonlyReason ?? statusDisabledReason

  return (
    <StatusHeaderCard
      action={
        <VStack alignItems="flex-end" gap={1} maxWidth="28em">
          {submitDisabledReason !== undefined ? (
            <ButtonTooltip content={submitDisabledReason}>
              {submitButton}
            </ButtonTooltip>
          ) : (
            submitButton
          )}
          {/* 受領済は文を出さない (ツールチップのみ。 showsSubmitBlockReasonText) */}
          {submitDisabledReason !== undefined &&
            readonlyReason === undefined &&
            showsSubmitBlockReasonText(status) && (
              <ActionReasonText textAlign="right">
                {submitDisabledReason}
              </ActionReasonText>
            )}
        </VStack>
      }
      items={items}
    />
  )
}
